import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { VIEW_CONSOLE, VIEW_HOME, VIEW_WELCOME } from '../../common/EsConstants';
import { esRead } from '../../common/EsFetch';
import { useEsScreenBarFlag } from '../../common/EsFlags';
import { useAuth } from '../parent/EsAuth';
import { API_CAMPAIGN_MEMORY_LAST, API_CAMPAIGN_MEMORY_SESSION, esCloseSession, readClosedSession,
    subscribeClosedSession } from './EsGameMemory';

// Once per page load: a press on the logo later is a visit to the front page, not a
// sign-in, and must stay there.
let asked = false;

/** A new page load, as far as this is concerned: for the tests, which share one module. */
export const esForgetLastGame = () => { asked = false; };

/**
 * Signing in, or opening the site signed in, lands on the last game while its session
 * runs (T6, rolloutEsScreenBar). Effect-only, in the shell.
 *
 * <p>Only from the page the load began on, and only when that is the front page or the
 * welcome page -- where a sign-in arrives, by password (EsLogin's reload) and by Google
 * alike. A login sent from a gated page goes back there (esLoginDestination), and a deep
 * link goes where it points. If the reader has moved on by the time esback answers, they
 * stay where they went.
 *
 * <p>esback decides "last" and "running" (EsCampaignMemoryService.lastGame) and refuses
 * with the flag off, so a flag that reads true for the wrong context a moment early
 * lands nobody anywhere.
 */
export default function EsLastGame() {
    const auth = useAuth();
    const on = useEsScreenBarFlag();
    const location = useLocation();
    const navigate = useNavigate();

    // The entry the load began on. A reload keeps history's key, so "default" cannot say it.
    const first = useRef( location.key );
    const here = useRef( location.key );
    here.current = location.key;

    const signedIn = Boolean( auth && !auth.loading && auth.account );
    const atLanding = location.key === first.current
        && ( location.pathname === VIEW_HOME || location.pathname === VIEW_WELCOME );

    useEffect( () => {
        if ( asked || !on || !signedIn || !atLanding ) return;
        asked = true;

        esRead( API_CAMPAIGN_MEMORY_LAST )
            .then( ( pLast ) => {
                if ( pLast?.key && here.current === first.current ) {
                    void navigate( VIEW_CONSOLE + '/' + pLast.key, { replace: true } );
                }
            } )
            .catch( () => {} );
    }, [ on, signedIn, atLanding, navigate ] );

    return null;
}

// The game page's own word on its session: { account, key, name, running, at }, from its poll and its
// writes. Newer than any session answer while the page is open, and kept after it closes,
// until an answer asked later replaces it.
let live = null;
const liveListeners = new Set();

const subscribeLive = ( pListener ) => {
    liveListeners.add( pListener );
    return () => liveListeners.delete( pListener );
};

const readLive = () => live;

/**
 * The game page says whether its session runs: EsGame on every read and write it lands. A
 * Start pressed here, or a poll that hears one, shows the nav's Session at once; an End
 * takes it away.
 */
export function esGameLive( pAccountId, pKey, pName, pRunning ) {
    const aRunning = Boolean( pRunning );
    if ( live && live.account === pAccountId && live.key === pKey && live.name === pName && live.running === aRunning ) return;
    live = { account: pAccountId, key: pKey, name: pName, running: aRunning, at: Date.now() };
    liveListeners.forEach( ( pListener ) => pListener() );
}

/** A new page load, as far as the nav's Session is concerned: for the tests. */
export const esForgetGameLive = () => { live = null; };

// How often the nav asks again while it shows a Session and the reader is outside that game,
// so a session ended at another seat goes without a click.
const SESSION_RECHECK_MS = 60000;

/**
 * The pick behind the nav's Session: the game page's word for its own game, and for any game
 * when it is newer than the answer; esback's answer otherwise. A Session closed on the nav is
 * out of both until a word newer than esback's having the close names it again -- a new
 * session there, since esback skips the one closed. Pure, for the tests.
 *
 * @param pAnswer { game, asked }: esback's session game (null for none) and when it was asked
 * @param pLive   the game page's word, or null
 * @param pHere   the key of the game on screen, or null
 * @param pClosed { key, at }: the game whose Session was closed and when esback had it, or null
 */
export function esSessionGame( pAnswer, pLive, pHere, pClosed = null ) {
    const aShut = ( pKey, pAt ) => Boolean( pClosed ) && pKey === pClosed.key && pAt <= pClosed.at;
    const aAnswer = pAnswer?.game && aShut( pAnswer.game.key, pAnswer.asked ) ? { ...pAnswer, game: null } : pAnswer;
    const aLive = pLive && aShut( pLive.key, pLive.at ) ? null : pLive;

    const aLiveWins = aLive && ( aLive.key === pHere || aLive.at > ( aAnswer?.asked ?? 0 ) );
    if ( !aLiveWins ) return aAnswer?.game ?? null;
    if ( aLive.running ) return { key: aLive.key, name: aLive.name };
    const aGame = aAnswer?.game ?? null;
    return aGame && aGame.key !== aLive.key ? aGame : null;
}

/**
 * The game the nav's Session goes back to: the account's running session -- esback's
 * campaignMemory/session, the most recently opened game with a session running where the
 * account plays a character or is the GM -- corrected by the game page's own word while it is
 * open. Asked again on every route, on a
 * game page too, and every minute while it shows outside that game. Null signed out, with the
 * flag off, and with nothing running.
 *
 * <p>Answers { game, close }. close() closes that game's Session on the nav and takes esback's
 * answer for the table it falls through to; it resolves once esback has it.
 */
export function useEsLastGame() {
    const auth = useAuth();
    const on = useEsScreenBarFlag();
    const { pathname } = useLocation();
    const [ answer, setAnswer ] = useState( null );
    const [ tick, setTick ] = useState( 0 );
    const gameLive = useSyncExternalStore( subscribeLive, readLive, readLive );
    const closed = useSyncExternalStore( subscribeClosedSession, readClosedSession, readClosedSession );

    const accountId = auth?.account?.accountId ?? null;
    const ask = Boolean( on && auth?.authenticated && !auth.loading );
    const here = pathname.startsWith( VIEW_CONSOLE + '/' )
        ? decodeURIComponent( pathname.slice( VIEW_CONSOLE.length + 1 ).split( '/' )[ 0 ] ) : null;

    // An answer asked earlier never replaces one asked later: a route's read that crosses a
    // close would otherwise put back what the close's own answer took away.
    const take = useCallback( ( pGame, pAsked ) => setAnswer( ( pNow ) =>
        ( pNow && pNow.asked > pAsked ? pNow : { game: pGame?.key ? pGame : null, asked: pAsked } ) ), [] );

    useEffect( () => {
        if ( !ask ) return undefined;
        let aLive = true;
        const aAsked = Date.now();
        esRead( API_CAMPAIGN_MEMORY_SESSION )
            .then( ( pLast ) => { if ( aLive ) take( pLast, aAsked ); } )
            .catch( () => { if ( aLive ) take( null, aAsked ); } );
        return () => { aLive = false; };
    }, [ ask, pathname, accountId, tick, take ] );

    // Another account's word, from before a sign-out in this tab, is no word at all.
    const game = ask ? esSessionGame( answer, gameLive?.account === accountId ? gameLive : null, here,
        closed?.account === accountId ? closed : null ) : null;
    const away = Boolean( game ) && game.key !== here;

    useEffect( () => {
        if ( !away ) return undefined;
        const aTimer = setInterval( () => setTick( ( pTick ) => pTick + 1 ), SESSION_RECHECK_MS );
        return () => clearInterval( aTimer );
    }, [ away ] );

    // The game page's word carries no campaign id; esback's answer for the same table does.
    const campaignId = game?.campaignId ?? ( answer?.game?.key === game?.key ? answer?.game?.campaignId : undefined );

    const close = useCallback( async () => {
        if ( !game ) return;
        const aClosed = await esCloseSession( accountId, { key: game.key, campaignId } );
        // Stamped after esback answered, so it outranks a route's read that crossed the close.
        if ( aClosed ) take( aClosed.game, Date.now() );
    }, [ game, campaignId, accountId, take ] );

    return { game, close };
}
